export default function BlogContent() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <article className="prose prose-lg max-w-none">
          <div className="text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 font-medium">
              Urban density in the Lower Mainland means smaller lots and more complex slab designs. When you're building in Vancouver's high-density zones, every square inch counts—and so does every line on your formwork drawings.
            </p>

            <p>
              At <strong>Dinari Designs</strong>, we specialize in managing the intricate geometries and tight tolerances that define modern urban construction. Here's how we use <strong>AutoCAD</strong> to turn complex challenges into buildable solutions:
            </p>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-building-line text-white text-xl"></i>
                </span>
                The Challenge of High-Density Construction
              </h2>
              <p className="text-gray-700 mb-0">
                Smaller lots mean tighter working spaces, more complex slab edges, and intricate column placements. Traditional "eyeball" layouts simply don't cut it when you're working with <strong>millimeter-level precision</strong> in downtown Vancouver or Burnaby's urban core.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-focus-3-line text-white text-xl"></i>
                </span>
                Tight Tolerances Require CAD Precision
              </h2>
              <p className="text-gray-700 mb-0">
                Our <strong>AutoCAD formwork drawings</strong> account for every bulkhead, slab edge, and penetration. We work directly from your architectural and structural plans to ensure that formwork fits perfectly—even in the most constrained spaces. This level of detail prevents costly field adjustments and keeps your crew moving forward.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-git-merge-line text-white text-xl"></i>
                </span>
                Clash Detection Before the Pour
              </h2>
              <p className="text-gray-700 mb-0">
                One of the biggest advantages of professional CAD layouts is <strong>clash detection</strong>. We identify conflicts between structural steel, MEP penetrations, and formwork placement <em>before</em> your crew arrives on site. This proactive approach saves time, reduces rework, and prevents the dreaded "we can't pour today" scenario.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-stack-line text-white text-xl"></i>
                </span>
                Multi-Level Coordination
              </h2>
              <p className="text-gray-700 mb-0">
                High-density projects often involve multiple levels being poured simultaneously. Our drawings include <strong>construction sequencing</strong> that shows how formwork moves from level to level, ensuring that your crew knows exactly what goes where—and when.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-map-pin-line text-white text-xl"></i>
                </span>
                Local Expertise Matters
              </h2>
              <p className="text-gray-700 mb-0">
                We understand the unique challenges of <strong>Vancouver's high-density construction</strong> environment. From the tight lots in Mount Pleasant to the complex podium structures in Metrotown, we've seen it all—and we know how to design formwork that works in real-world conditions.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 my-12 text-white">
              <h2 className="text-3xl font-bold mb-4">
                Ready to lean out your overhead?
              </h2>
              <p className="text-lg mb-6 text-teal-50">
                Don't let tight tolerances and intricate geometries slow you down. Our <strong>AutoCAD formwork layouts</strong> are designed for the realities of high-density construction in Vancouver and the Lower Mainland.
              </p>
              <a 
                href="/home#contact" 
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Get a Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-500">Share this article:</span>
                  <div className="flex space-x-3">
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-twitter-x-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-facebook-fill"></i>
                    </a>
                  </div>
                </div>
                <a 
                  href="/knowledge-hub" 
                  className="text-teal-600 hover:text-teal-700 font-medium flex items-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Knowledge Hub
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
